import difference from 'lodash/difference';
import filter from 'lodash/filter';
import keys from 'lodash/keys';
import find from 'lodash/find';
import omit from 'lodash/omit';
import lowerCase from 'lodash/lowerCase';
import isObjectLike from 'lodash/isObjectLike';
import { getFieldsWithSuggestions, getSampleDataFromCsv } from 'modules/catalogFieldTypes/helpers';
import { TableCell } from 'ui/tableCell';

/**
 * getNewFieldsFromCsv
 * helper functions that returns the new fields from a csv
 */
export const getNewFieldsFromCsv = (props, meta) => {
  const { definedFields } = props;
  const fields = filter(meta.fields, (f) => f.toLowerCase() !== 'key');
  // TODO it might be necessary to also get undefined keys from store as this will always have top-level objects be considered "defined"
  return difference(fields, keys(definedFields));
};

/**
 * getPreviewTableColumns
 * helper fucntion used to populate the columsn for the preview table
 * displayed in the CatalogCsvUploader when a catalog is updated with
 * new entries via csv.
 */
export const getPreviewTableColumns = (fields) => {
  return fields?.map((fieldValue, i) => ({
    id: i, // id is required for the table rows to render without any errors in the console due to repeated rows with a same key
    name: fieldValue,
    value: fieldValue,
    sortable: false,
    align: 'flex-start',
    RowCell: ({ row }) => <TableCell>{row[fieldValue]}</TableCell>,
  }));
};

/**
 * getRows
 * helper functions that returns the rows from the rows object
 */
export const getRows = (rows) =>
  rows.map((row) => keys(row).reduce((acc, key) => ({ ...acc, [key]: row[key] }), {}));

/**
 * doColsNotHaveKey
 * helper function that validates whether columns have keyhs, and if not, fires a cb fn
 */
export const doColsNotHaveKey = (props, cols) => {
  const hasNoKey = !find(cols, (col) => lowerCase(col.value) === 'key');
  if (hasNoKey) {
    props.csvKeyMissing();
  }
  return hasNoKey;
};

/**
 * doColsNotHaveValues
 * helper function that validates whether columns have values
 */
export const doColsNotHaveValues = (cols) => isObjectLike(find(cols, { value: '' }));

/**
 * getHandleStepAndSkipState
 * helper function that gets the state when handleStepAndSkip is called
 */
export const getHandleStepAndSkipState = (state, step, skipDefine) => {
  const prevStep = state.step;
  const toUpdate = { step, skipDefine: skipDefine === undefined ? false : skipDefine };
  switch (step) {
    case 1:
      return { ...toUpdate, fields: {} };
    case 2:
      // do not get auto-suggestions if going backwards from step 3 to step 2 (should instead preserve whatever changes the user has made to the fields)
      if (!skipDefine && prevStep !== 3) {
        const { fields, newCsvFields, csvRowData } = state;
        const rows = getSampleDataFromCsv({ newFields: newCsvFields, rowData: csvRowData });
        const { suggestedTypes, updatedFields } = getFieldsWithSuggestions({ rows, fields });
        return { ...toUpdate, suggestedTypes, fields: updatedFields };
      }
      return skipDefine ? { ...toUpdate, fields: {} } : toUpdate;
    case 3:
    default:
      return skipDefine ? { ...toUpdate, fields: {} } : toUpdate;
  }
};

/**
 * checkIfIsDisabled
 * helper function that validates whether state should be disabled
 */
export const checkIfIsDisabled = (state) => {
  const { isSubmitting, csvFile, keyWarning, headerValueWarning } = state;
  return isSubmitting || !csvFile || keyWarning || headerValueWarning;
};

/**
 * getHandleOnChangeFieldTypesDropdownState
 * helper function gets the state for when the field type dropdown changes
 */
export const getHandleOnChangeFieldTypesDropdownState = (state, val, key) => {
  const { fields } = state;
  return val ? { fields: { ...fields, [key]: val } } : { fields: omit(fields, key) };
};
